import React, { useEffect, useMemo, useCallback } from 'react';
import { EditorContent, EditorContext, useEditor, generateJSON } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { Color } from '@tiptap/extension-color';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import styled from 'styled-components';
import { MenuBar } from './components/editor-bar';
import { Body2Styles } from '../typography';
import { getColor, getColorShade, getSizeBy, getThemeProp } from '../../../src/styled/utils';
export function RichTextEditor(props) {
    var _a;
    const editor = useInventEditor(props);
    const providerValue = useMemo(() => ({ editor }), [editor]);
    if (!editor) {
        return null;
    }
    return (React.createElement(EditorContext.Provider, { value: providerValue },
        React.createElement(RichTextEditorRoot, { "data-testid": "rich-text-editor-root", className: props.className, disabled: props.disabled, "$invalid": props.invalid }, (_a = props === null || props === void 0 ? void 0 : props.MenuBar) !== null && _a !== void 0 ? _a : React.createElement(MenuBar, null),
            React.createElement(StyledEditorContent, { editor: editor, "data-testid": "rich-text-editor-content" }))));
}
function useInventEditor(props) {
    const isValidHTML = useCallback((str) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(str, 'text/html');
        const hasValidTags = doc.body.children.length > 0;
        const isValid = !doc.querySelector('parsererror') && hasValidTags;
        return isValid;
    }, []);
    const htmlToProseMirrorJSON = useCallback((editor, html) => {
        const extensions = editor.extensionManager.extensions;
        return generateJSON(html, extensions);
    }, []);
    const content = useMemo(() => {
        return isValidHTML(props.value) ? props.value : `<p>${props.value}</p>`;
    }, [props.value, isValidHTML]);
    const editor = useEditor({
        content: content,
        onUpdate: ({ editor }) => {
            props.onChange(editor.getHTML());
        },
        extensions: [
            ...extensions,
            Placeholder.configure({
                placeholder: props.placeholder
            })
        ],
        editable: !props.disabled
    });
    useEffect(() => {
        if (!editor)
            return;
        const incomingRawValue = props.value;
        if (incomingRawValue === editor.getHTML())
            return;
        const currentContent = editor.getJSON();
        const incomingContent = htmlToProseMirrorJSON(editor, incomingRawValue);
        const sameJson = JSON.stringify(currentContent) == JSON.stringify(incomingContent);
        if (!sameJson)
            editor.commands.setContent(incomingRawValue);
    }, [props.value, editor]);
    return editor;
}
const extensions = [
    StarterKit.configure({
        bulletList: {
            keepMarks: true,
            keepAttributes: false
        },
        orderedList: {
            keepMarks: true,
            keepAttributes: false
        }
    }),
    Image.configure({
        allowBase64: true,
        inline: true
    }),
    Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https'
    }),
    Color,
    Underline
];
export const BaseInputWrapper = styled.div `
  ${Body2Styles};
  position: relative;
  display: block;
  width: 100%;
  padding: ${getSizeBy(3)};
  background: ${({ disabled }) => disabled ? getColorShade('onSurface', 'hover') : getColor('surface')};
  border-color: ${({ $invalid }) => {
    if ($invalid)
        return getColorShade('danger', 'highEmphasis');
    return getColorShade('onSurface', 'outline');
}};
  border-style: solid;
  border-width: 1px;
  border-radius: ${getThemeProp('borderRadius')}px;
`;
const RichTextEditorRoot = styled(BaseInputWrapper).attrs({
    as: 'div'
}) `
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 182px;
  height: 200px;
  min-height: 100px;
`;
const StyledEditorContent = styled(EditorContent) `
  height: 100%;
  overflow-y: auto;
  color: ${getColor('onSurface')};

  .tiptap {
    flex-grow: 1;
    height: 100%;
    outline: none;

    strong,
    em,
    a,
    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    ul,
    li {
      all: revert;
    }

    p.is-editor-empty:first-child::before {
      float: left;
      height: 0;
      color: #adb5bd;
      content: attr(data-placeholder);
      pointer-events: none;
    }

    a {
      color: ${(props) => props.theme.colors.primary};
    }
  }
`;
